import { useEffect, useState } from 'react';
import { Row, Col, Container, Button, Form, Card } from 'react-bootstrap';
import api from '../api/CallManagerApi';
import { toast } from 'react-toastify';
import { toastConfig } from '../utils';
import BackButton from '../components/BackButton';
import SelectListGroup from '../components/SelectChangeUserInfoListGroup';
import ProfilePasswordComponent from '../components/ProfilePasswordComponent';
import NotificationMailComponent from '../components/NotificationMailComponent';
import { useDispatch } from 'react-redux';
import { setGlobalDisplayName } from '../../store/displayNameReducer';
import { useErrorBoundary } from 'react-error-boundary';
import { ManagerAdminUserInfo } from '../interface/ManagerAdminUserInfo';
import { Institution } from '../interface/Institution';
import { validateInputs } from '../validateInputs';
import { startLoading, stopLoading } from '../../store/loadingReducer';
import { ACCOUNT } from '../enum/AccountEnum';
import { PHASE } from '../enum/PhaseEnum';
import Axios from 'axios';

const ShowUser: React.FunctionComponent = () => {
  const [institutionName, setInstitutionName] = useState<string>('');
  const [responsibleTerm, setResponsibleTerm] = useState<{ year: number; month: number }>();
  const [managerName, setManagerName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isDisplayUserInfo, setIsDisplayUserInfo] = useState<boolean>(true);
  const [isDisplayChangePassword, setIsDisplayChangePassword] = useState<boolean>(false);
  const [isDisplayNotificationMail, setIsDisplayNotificationMail] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<any>({});
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    (async () => {
      dispatch(startLoading());
      const profile: ManagerAdminUserInfo = await api.getProfile();
      setManagerName(profile.name);
      setPhone(profile.phone);
      setEmail(profile.email);
      const institution: Institution = await api.getInstitution(profile.institution);
      setInstitutionName(institution.name);
      setResponsibleTerm(institution.responsibleTerm);
      setErrorMessages({});
      dispatch(stopLoading());
    })();
  }, []);

  const changeUserInfo = async () => {
    const checkParams = { managerName, phone, email };
    const [isError, errorMessages] = validateInputs(checkParams);
    if (isError) {
      setErrorMessages(errorMessages);
      return;
    }
    setErrorMessages({});

    try {
      await api.putProfile(managerName, phone, email);
      dispatch(setGlobalDisplayName(managerName));
      toast.success('ユーザー情報の変更が完了しました。', toastConfig());
    } catch (e: any) {
      if (!Axios.isAxiosError(e)) showBoundary(e);
      if (e.response.status === 400) {
        toast.error('ユーザー情報の変更に失敗しました。', toastConfig());
        return;
      }
      showBoundary(e.response);
    }
  };

  const selectDisplayInfo = () => {
    setIsDisplayUserInfo(true);
    setIsDisplayChangePassword(false);
    setIsDisplayNotificationMail(false);
  };

  const selectChangePassword = () => {
    setIsDisplayUserInfo(false);
    setIsDisplayChangePassword(true);
    setIsDisplayNotificationMail(false);
  };

  const selectNotificationMail = () => {
    setIsDisplayUserInfo(false);
    setIsDisplayChangePassword(false);
    setIsDisplayNotificationMail(true);
  };

  return (
    <>
      <Container>
        <Row>
          <Col>
            <div className="ta-left">
              <BackButton />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <SelectListGroup
              DisplayUserInfoName="教育機関担当者"
              isDisplayUserInfo={isDisplayUserInfo}
              isDisplayChangePassword={isDisplayChangePassword}
              isDisplayNotificationMail={isDisplayNotificationMail}
              isDisplayNotificationMailItem={true}
              selectDisplayInfo={selectDisplayInfo}
              selectChangePassword={selectChangePassword}
              selectNotificationMail={selectNotificationMail}
            />
          </Col>
          <Col sm={8}>
            {isDisplayUserInfo && (
              <Card>
                <Card.Body>
                  <Card.Title>教育機関担当者</Card.Title>
                  教育機関名
                  <Form.Group>
                    <Form.Control type="text" value={institutionName} disabled={true} />
                  </Form.Group>
                  報告対象期間
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={(responsibleTerm?.year ?? ' - ') + '年' + (responsibleTerm?.month ?? ' - ') + '月'}
                      disabled={true}
                    />
                  </Form.Group>
                  教育機関担当者名 <span className="badge badge-danger">必須</span>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={managerName}
                      isInvalid={errorMessages?.managerName}
                      onChange={(event) => {
                        setManagerName(event.target.value);
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{errorMessages.managerName}</Form.Control.Feedback>
                  </Form.Group>
                  連絡先電話番号 <span className="badge badge-danger">必須</span>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={phone}
                      isInvalid={errorMessages?.phone}
                      onChange={(event) => {
                        setPhone(event.target.value);
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{errorMessages.phone}</Form.Control.Feedback>
                  </Form.Group>
                  連絡先メールアドレス <span className="badge badge-danger">必須</span>
                  <Form.Group>
                    <Form.Control
                      type="text"
                      value={email}
                      isInvalid={errorMessages?.email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                      }}
                    />
                    <Form.Control.Feedback type="invalid">{errorMessages.email}</Form.Control.Feedback>
                  </Form.Group>
                  <div className="w-100 text-muted lh-1">SARTRASまたは委託先の輿論科学協会から、連絡を差し上げる場合がございます。</div>
                  <div className="ta-right">
                    <Button
                      variant="primary"
                      className="m-10"
                      onClick={() => {
                        changeUserInfo();
                      }}
                    >
                      保存
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
            {isDisplayChangePassword && <ProfilePasswordComponent api={api} />}
            {isDisplayNotificationMail && <NotificationMailComponent api={api} accountString={ACCOUNT.MANAGER} phaseString={PHASE.USER_CONFIRMED} />}
          </Col>
        </Row>
      </Container>
    </>
  );
};
export default ShowUser;
